// hook to get stock values, like if the item is in stock, and a text to display, based on Article with IArticlePriceStockModel

import { useState, useEffect, useCallback } from 'react'
import { IArticlePriceStockModel } from '@/types/api'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

export enum StockStatus {
  InStock = 'InStock',
  ToOrder = 'ToOrder',
  OnPurchase = 'OnPurchase',
  OutOfStock = 'OutOfStock',
}

export const useStock = (article: IArticlePriceStockModel | undefined, isReseller: boolean = false) => {
  const { t } = useTranslation('common')
  const [stockStatus, setStockStatus] = useState<StockStatus>(StockStatus.OutOfStock)
  const [stockText, setStockText] = useState<string>('')

  const calculateStock = useCallback(() => {
    if (!article) {
      setStockStatus(StockStatus.OutOfStock)
      setStockText('-')
      return
    }

    // Alwasys show stock for resellers
    if (isReseller && article.quantityAvailable >= 4) {
      const quantity = article.quantityAvailable >= 99 ? '99+' : article.quantityAvailable
      const inStockQuantityText = t('stock.inStockQuantity', { quantity })
      if (article.quantityAvailable < 4)
        setStockStatus(StockStatus.ToOrder)
      else
        setStockStatus(StockStatus.InStock)
      setStockText(inStockQuantityText)

    // Show estimated delivery date if quantity is less than 4
    } else if (article.estimatedDeliveryDate && article.quantityAvailable < 4) {
      const onPurchaseText = t('stock.onPurchase')
      const formattedEstimatedDeliveryDate = dayjs(article.estimatedDeliveryDate).format("YYYY-MM-DD")
      const stockText = `${onPurchaseText}: ${formattedEstimatedDeliveryDate}`
      setStockStatus(StockStatus.OnPurchase)
      setStockText(stockText)

    // Show low stock if quantity is less than 4
    } else if (article.quantityAvailable < 4 && article.quantityAvailable > 0) {
      const quantity = article.quantityAvailable
      const inStockQuantityText = t('stock.inStockQuantity', { quantity })
      setStockStatus(StockStatus.ToOrder)
      setStockText(inStockQuantityText)

    // Show estimated delivery date
    } else if (article.deliveryTime) {
      const toOrderText = t('stock.toOrder')
      const deliveryTimeText = article.deliveryTime.replace('DAYS', t('days')).replace('WEEKS', t('weeks'))
      const stockText = `${toOrderText}: ${deliveryTimeText}`
      setStockStatus(StockStatus.ToOrder)
      setStockText(stockText)

    // Show to order if quantity is 0
    } else if (article.quantityExternal) {
      const externalStockText = t('stock.toOrder')
      setStockStatus(StockStatus.ToOrder)
      setStockText(externalStockText)

    // Show in stock if quantity is more than 0
    } else if (article.quantityAvailable > 0) {
      const inStockQuantityText = t('stock.inStock')
      setStockStatus(StockStatus.InStock)
      setStockText(inStockQuantityText)

    // Show out of stock if quantity is 0
    } else {
      setStockStatus(StockStatus.OutOfStock)
      setStockText('-')
    }
  }, [article, isReseller, t])

  useEffect(() => {
    calculateStock()
  }, [calculateStock])

  return { stockStatus, stockText }
}

